import React, { useCallback } from 'react';
import { Button, Card, Popconfirm, Space, Table, Typography, message, Form, Input, Row, Col, Badge } from 'antd';
import { getDeviceFaultList, recoverDeviceFault, ignoreDeviceFault, DeviceFaultItem } from '@/api';
import { useTableRequest } from '@/hooks';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import useAuthButton from '@/hooks/useAuthButton';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;
const { useMessage } = message;

interface DeviceFaultProps {}

// 设备故障页面组件
const DeviceFault: React.FC<DeviceFaultProps> = () => {
  const [messageApi, contextHolder] = useMessage();
  const { checkPermissionDisabled } = useAuthButton();

  // 表格请求配置
  const { form, loading, dataSource, pagination, search, reset, reload } = useTableRequest<DeviceFaultItem>({
    onRequest: getDeviceFaultList,
  });

  // 恢复设备故障
  const handleRecover = useCallback(async (record: DeviceFaultItem) => {
    try {
      await recoverDeviceFault(record.device_sn, record.fault_code);
      messageApi.success('设备故障恢复成功');
      reload(); // 刷新表格数据
    } catch (error) {
      messageApi.error('设备故障恢复失败');
    }
  }, [messageApi, reload]);

  // 忽略设备故障
  const handleIgnore = useCallback(async (record: DeviceFaultItem) => {
    try {
      await ignoreDeviceFault(record.device_sn, record.fault_code);
      messageApi.success('设备故障已忽略');
      reload(); // 刷新表格数据
    } catch (error) {
      messageApi.error('设备故障忽略失败');
    }
  }, [messageApi, reload]);

  // 表格列配置
  const columns: ColumnsType<DeviceFaultItem> = [
    {
      title: '设备序列号',
      dataIndex: 'device_sn',
      key: 'device_sn',
      width: 200,
      ellipsis: true,
    },
    {
      title: '产品ID',
      dataIndex: 'product_id',
      key: 'product_id',
      width: 150,
    },
    {
      title: '故障代码',
      dataIndex: 'fault_code',
      key: 'fault_code',
      width: 180,
      render: (text: string) => (
        <Text code>{text}</Text>
      ),
    },
    {
      title: '故障标题',
      dataIndex: 'fault_title',
      key: 'fault_title',
      width: 200,
      render: (text: string) => (
        <Badge status="error" text={text} />
      ),
    },
    {
      title: '故障时间',
      dataIndex: 'fault_time',
      key: 'fault_time',
      width: 180,
      render: (time: number) => {
        return time ? dayjs(time * 1000).format('YYYY-MM-DD HH:mm:ss') : '-';
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created',
      key: 'created',
      width: 180,
      render: (time: number) => {
        return time ? dayjs(time * 1000).format('YYYY-MM-DD HH:mm:ss') : '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {!checkPermissionDisabled('device-fault:recover') && (
            <Popconfirm
              title="确认恢复"
              description="确定要恢复此设备故障吗？"
              onConfirm={() => handleRecover(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button 
                type="primary" 
                size="small" 
                icon={<CheckCircleOutlined />}
              >
                恢复
              </Button>
            </Popconfirm>
          )}
          {!checkPermissionDisabled('device-fault:ignore') && (
            <Popconfirm
              title="确认忽略"
              description="确定要忽略此设备故障吗？"
              onConfirm={() => handleIgnore(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button 
                size="small" 
                icon={<CloseCircleOutlined />}
              >
                忽略
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card>
        {/* 搜索表单 */}
        <Form
          form={form}
          layout="inline"
          onFinish={search}
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[16, 16]} style={{ width: '100%' }}>
            <Col span={6}>
              <Form.Item name="device_sn" label="设备序列号">
                <Input placeholder="请输入设备序列号" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="product_id" label="产品ID">
                <Input placeholder="请输入产品ID" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="fault_code" label="故障代码">
                <Input placeholder="请输入故障代码" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Space>
                <Button type="primary" htmlType="submit">
                  查询
                </Button>
                <Button onClick={reset}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>

        {/* 数据表格 */}
        <Table
          loading={loading}
          dataSource={dataSource}
          pagination={pagination}
          columns={columns}
          rowKey={(record) => `${record.device_sn}-${record.fault_code}`}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>
    </>
  );
};

export default DeviceFault;
